import type { PublicacaoResponse } from '@triade/contracts';

/**
 * Geometria do calendario de publicacoes: so datas (dias do mes), nunca preco. Datas ISO ("2026-12-20") viram numero de dia
 * em UTC (aritmetica exata, sem fuso). Posicao e largura das barras sao fracoes dos dias do mes.
 */

export interface MesRef {
  ano: number;
  /** 1 a 12. */
  mes: number;
}

const DIA_MS = 86_400_000;
const COR_TOTAL = 8;

const dois = (n: number) => String(n).padStart(2, '0');

/** Dia civil como inteiro (dias desde 1970-01-01, UTC). Aceita "YYYY-MM-DD" ou instante ISO (usa so a data). */
export function diaNumero(iso: string): number {
  const [ano = 0, mes = 1, dia = 1] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(ano, mes - 1, dia) / DIA_MS;
}

export function diasDoMes({ ano, mes }: MesRef): number {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

export function primeiroDiaIso(m: MesRef): string {
  return `${m.ano}-${dois(m.mes)}-01`;
}

export function ultimoDiaIso(m: MesRef): string {
  return `${m.ano}-${dois(m.mes)}-${dois(diasDoMes(m))}`;
}

export function mesVizinho(m: MesRef, delta: number): MesRef {
  const indice = m.ano * 12 + (m.mes - 1) + delta;
  return { ano: Math.floor(indice / 12), mes: (indice % 12) + 1 };
}

/** Mes de uma data ISO. */
export function mesDoIso(iso: string): MesRef {
  return { ano: Number(iso.slice(0, 4)), mes: Number(iso.slice(5, 7)) };
}

/** Data de hoje no fuso LOCAL (Brasil), "YYYY-MM-DD". So para comparar com as datas da API. */
export function isoHoje(agora: Date = new Date()): string {
  return `${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`;
}

/** Dia da semana da data de inicio do mes, com segunda = 0 (grade pt-BR). */
export function deslocamentoSemana(m: MesRef): number {
  return (new Date(Date.UTC(m.ano, m.mes - 1, 1)).getUTCDay() + 6) % 7;
}

export interface BarraCalendario {
  publicacao: PublicacaoResponse;
  /** Cor da publicacao: 1 a 8 (classe `bg-cal-N`). Ciclica se houver mais de 8 publicacoes no mes. */
  cor: number;
  /** Primeiro dia exibido, 0 = dia 1 do mes. */
  inicio: number;
  /** Ultimo dia exibido, inclusive. */
  fim: number;
  /** Vigencia comeca antes do mes: a barra corta na borda esquerda. */
  inicioAberto: boolean;
  /** Termina depois do mes ou nao tem fim: a barra corta na borda direita (ponta aberta). */
  fimAberto: boolean;
  /** Sem `validAte`. */
  semFim: boolean;
  /** Faixa vertical (0 = primeira). Barras na mesma faixa nunca compartilham dia. */
  faixa: number;
  /** Posicao e largura em % da largura do mes (so geometria de dias). */
  esquerdaPct: number;
  larguraPct: number;
}

export interface CalendarioMes {
  dias: number;
  barras: BarraCalendario[];
  /** Quantidade de faixas empilhadas (0 quando o mes nao tem publicacoes). */
  faixas: number;
}

/**
 * Monta as barras do mes: so as publicacoes que tocam o mes. Ordena por inicio (depois por id, estavel) e empilha em faixas
 * com o algoritmo guloso (primeira faixa livre). Publicacao sem fim vai ate o ultimo dia do mes com ponta aberta.
 */
export function montarCalendarioMes(publicacoes: PublicacaoResponse[], m: MesRef): CalendarioMes {
  const dias = diasDoMes(m);
  const base = diaNumero(primeiroDiaIso(m));
  const ordenadas = [...publicacoes].sort((x, y) => (x.validFrom === y.validFrom ? x.id.localeCompare(y.id) : x.validFrom.localeCompare(y.validFrom)));

  const faixasFim: number[] = [];
  const barras: BarraCalendario[] = [];
  ordenadas.forEach((publicacao, i) => {
    const offsetInicio = diaNumero(publicacao.validFrom) - base;
    const offsetFim = publicacao.validAte ? diaNumero(publicacao.validAte) - base : Number.POSITIVE_INFINITY;
    if (offsetInicio > dias - 1 || offsetFim < 0) return;

    const inicio = Math.max(offsetInicio, 0);
    const fim = Math.min(offsetFim, dias - 1);
    let faixa = faixasFim.findIndex((ate) => ate < inicio);
    if (faixa === -1) faixa = faixasFim.length;
    faixasFim[faixa] = fim;

    barras.push({
      publicacao,
      cor: (i % COR_TOTAL) + 1,
      inicio,
      fim,
      inicioAberto: offsetInicio < 0,
      fimAberto: publicacao.validAte === null || offsetFim > dias - 1,
      semFim: publicacao.validAte === null,
      faixa,
      esquerdaPct: (inicio / dias) * 100,
      larguraPct: ((fim - inicio + 1) / dias) * 100,
    });
  });

  return { dias, barras, faixas: faixasFim.length };
}

/** Barras que cobrem o dia (0 = dia 1), para os pontos da grade. */
export function barrasDoDia(barras: BarraCalendario[], indice: number): BarraCalendario[] {
  return barras.filter((b) => b.inicio <= indice && indice <= b.fim);
}
